import React, { useEffect, useState } from "react";
import { ActivityIndicator, Pressable, Text, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import tw from "twrnc";

import { notify } from "@/components/shared/AppToast";
import { useActionSheet } from "@/components/hooks/useActionSheet";
import Button from "@/components/settings/Button";
import { colors, text } from "@/components/settings/theme";
import { t, tc, tn } from "@/localization";
import { leaveSharedAccount, refreshSharedAccess } from "./actions";
import { teamsErrorMessage } from "./errors";
import { openWorkspace, singleSharedSlug } from "./openWorkspace";
import RolePill from "./RolePill";
import { AccountAvatar, coverageText } from "./SharedWithYou";
import useWorkspace from "./useWorkspace";

const Row = ({
  selected,
  busy,
  avatar,
  title,
  pill,
  subtitle,
  onPress,
  right,
  label,
}) => (
  <Pressable
    onPress={onPress}
    accessibilityRole="button"
    accessibilityLabel={label}
    accessibilityState={{ selected: !!selected }}
    style={({ hovered, pressed }) => [
      tw`flex-row items-center px-3 py-3 rounded-lg`,
      {
        backgroundColor: selected
          ? colors.brandTint
          : hovered || pressed
          ? colors.surfaceStrong
          : "transparent",
      },
    ]}
  >
    {avatar}
    <View style={tw`flex-1 ml-3`}>
      <View style={tw`flex-row items-center`}>
        <Text style={[text.bodyStrong, tw`flex-shrink mr-2`]} numberOfLines={1}>
          {title}
        </Text>
        {pill}
      </View>
      {!!subtitle && (
        <Text style={[text.hint, tw`mt-0.5`]} numberOfLines={1}>
          {subtitle}
        </Text>
      )}
    </View>
    {busy ? (
      <ActivityIndicator size="small" color={colors.slate} style={tw`ml-2`} />
    ) : selected ? (
      <Ionicons
        name="checkmark-circle"
        size={20}
        color={colors.brand}
        style={tw`ml-2`}
      />
    ) : null}
    {right}
  </Pressable>
);

// Profile's account switcher (PLAN.md §5.5): your own account and every
// account or event shared with you, with your role there. Picking one opens
// it; Leave gives up access you no longer need.
const AccountSwitcher = ({ navigation, style }) => {
  const ws = useWorkspace();
  const { showActionSheetWithOptions } = useActionSheet();
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (ws.enabled && ws.self) refreshSharedAccess();
  }, [ws.enabled, ws.self]);

  if (!ws.enabled || (!ws.accounts.length && !ws.isShared)) return null;

  const open = async (owner, slug) => {
    if (busy) return;
    setBusy(owner || "own");
    try {
      await openWorkspace(navigation, owner, { slug, source: "profile" });
    } finally {
      setBusy(null);
    }
  };

  const leave = (entry) => {
    showActionSheetWithOptions(
      {
        options: [tc("account", "Leave"), t("Cancel")],
        cancelButtonIndex: 1,
        destructiveButtonIndex: 0,
        title: entry.accountRole
          ? t("Leave {team}'s account?", { team: entry.name })
          : tn(
              "Stop working on {team}'s event?",
              "Stop working on {team}'s events?",
              entry.events.length,
              { team: entry.name }
            ),
        message: t("You'd need a new invite to get access again."),
      },
      async (index) => {
        if (index !== 0) return;
        setBusy(entry.owner);
        try {
          await leaveSharedAccount(entry);
          notify.success(
            t("Done"),
            t("You left {team}'s account.", { team: entry.name })
          );
        } catch (err) {
          notify.error(t("Couldn't leave"), teamsErrorMessage(err));
        } finally {
          setBusy(null);
        }
      }
    );
  };

  return (
    <View style={style}>
      <Text style={[text.label, tw`mb-1.5 px-3`]}>{t("Accounts")}</Text>
      <Row
        selected={!ws.isShared}
        busy={busy === "own"}
        avatar={
          <View
            style={[
              tw`items-center justify-center`,
              {
                width: 36,
                height: 36,
                borderRadius: 18,
                backgroundColor: colors.surfaceStrong,
              },
            ]}
          >
            <Ionicons name="person-outline" size={18} color={colors.slate} />
          </View>
        }
        title={t("My account")}
        subtitle={t("Your own events, plan and purchases")}
        label={t("Use my own account")}
        onPress={() => open(null)}
      />
      {ws.accounts.map((entry) => (
        <Row
          key={entry.owner}
          selected={ws.owner === entry.owner}
          busy={busy === entry.owner}
          avatar={<AccountAvatar entry={entry} />}
          title={entry.name}
          pill={<RolePill role={entry.role} />}
          subtitle={coverageText(entry)}
          label={t("Work in {team}'s account", { team: entry.name })}
          onPress={() => open(entry.owner, singleSharedSlug(entry))}
          right={
            <Button
              text={tc("account", "Leave")}
              variant="tertiary"
              size="sm"
              fullWidth={false}
              style={tw`ml-1`}
              accessibilityLabel={t("Leave {team}'s account", {
                team: entry.name,
              })}
              onPress={() => leave(entry)}
            />
          }
        />
      ))}
      {!!ws.sharedError && (
        <Text style={[text.hint, tw`mt-1 px-3`]}>{ws.sharedError}</Text>
      )}
    </View>
  );
};

export default AccountSwitcher;
